/* global eventSlug showRevDepartmentsUrl showTicketingUrl showSponsorshipUrl fetchReportingOverviewUrl vikEnabled markUpCategories markUpCategoriesExists agencyMode marginDisplayBasis */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { parseLocaleNumber } from 'Helpers/currencyFormat';
import { formatFigure, formatOverviewFigure } from 'Helpers/formatService';
import { marginPercentage, MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';
import { DISPLAY_UNIT, setDisplayUnit, useDisplayUnit } from 'Helpers/overviewDisplayUnit';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import SegmentedControl from 'CommonComponents/SegmentedControl';
import TableFilterModal from 'CommonComponents/TableFilterModal';
import TableSearchInput from 'CommonComponents/TableSearchInput';
import Tooltip from 'CommonComponents/Tooltip';

import CircleHelpIconUrl from 'Assets/icons/circle-help.svg?url';
import MinusIcon from 'Assets/icons/minus.svg';
import PlusIcon from 'Assets/icons/plus.svg';

const eventLocalStorageKey = typeof eventSlug === 'undefined' ? null : 'expenditure-filters-event-summary-' + eventSlug;
const expenditureFiltersEvent = eventLocalStorageKey ? JSON.parse(localStorage.getItem(eventLocalStorageKey)) : null;

const COLUMNS = {
    committed: { label: $t('Committed'), checked: expenditureFiltersEvent ? expenditureFiltersEvent.committed.checked : true },
    uncommitted: { label: $t('Uncommitted'), checked: expenditureFiltersEvent ? expenditureFiltersEvent.uncommitted.checked : true },
    risks: { label: $t('Risk'), checked: expenditureFiltersEvent ? expenditureFiltersEvent.risks.checked : true },
    opportunities: { label: $t('Opportunities'), checked: expenditureFiltersEvent ? expenditureFiltersEvent.opportunities.checked : true },
    forecast: { label: $t('Forecast'), checked: expenditureFiltersEvent ? expenditureFiltersEvent.forecast.checked : true },
};

/** Columns agency mode drops, so their toggles come out of the filter modal too. */
const AGENCY_HIDDEN_COLUMNS = ['risks', 'opportunities', 'forecast'];

// Breakdown ⇄ Cashflow (Total) view — persisted under a key distinct from the
// expenditure/revenue DataTables toggles so the Overall Budget preference is its own.
const OVERVIEW_VIEW_KEY = 'ib_overview_view';
const VIEW_BREAKDOWN = 'breakdown';
const VIEW_CASHFLOW = 'cashflow';

// "Hide empty columns" preference (Breakdown view only) — on by default, persisted
// per-browser.
const OVERVIEW_HIDE_EMPTY_KEY = 'ib_overview_hide_empty';

function readStoredHideEmpty() {
    try {
        const stored = localStorage.getItem(OVERVIEW_HIDE_EMPTY_KEY);
        return stored === null ? true : stored === 'true';
    } catch (e) {
        return true;
    }
}

// ─── Cashflow layer control (IB-654) ──────────────────────────────────
const OVERVIEW_LAYER_TOTAL = 'total';
const OVERVIEW_LAYER_SPLIT = 'split';
const OVERVIEW_LAYER_SPLITFUND = 'splitfund';

// The month map a row renders under a layer: Total and both split modes keep the
// main row on `months`; a single layer swaps to that layer's map.
function overviewLayerMonths(department, layer) {
    if (!department) return {};
    if (layer === OVERVIEW_LAYER_TOTAL || layer === OVERVIEW_LAYER_SPLIT || layer === OVERVIEW_LAYER_SPLITFUND) {
        return department.months || {};
    }
    return (department.layers && department.layers[layer]) || {};
}

// The two [layerKey, label] split sub-rows for the active layer, or null.
function overviewSplitPair(layer, labels) {
    if (layer === OVERVIEW_LAYER_SPLIT) {
        return [
            ['committed', labels.committed],
            ['uncommitted', labels.uncommitted],
        ];
    }
    if (layer === OVERVIEW_LAYER_SPLITFUND) {
        return [
            ['cash', labels.cash],
            ['vik', labels.vik],
        ];
    }
    return null;
}

function readStoredOverviewViewMode() {
    try {
        return localStorage.getItem(OVERVIEW_VIEW_KEY) === VIEW_CASHFLOW ? VIEW_CASHFLOW : VIEW_BREAKDOWN;
    } catch (e) {
        return VIEW_BREAKDOWN;
    }
}

export default function ReportingOverviewTable(props) {
    const [collapsed, setCollapsed] = useState(false);
    const [reporting, setReporting] = useState();
    const [loading, setLoading] = useState(true);
    const [columns, setColumns] = useState(COLUMNS);
    const [filters, setFilters] = useState({ search: '' });
    const [viewMode, setViewMode] = useState(readStoredOverviewViewMode);
    const [hideEmpty, setHideEmpty] = useState(readStoredHideEmpty);
    const unit = useDisplayUnit();
    // Cashflow layer (Total / Committed / Uncommitted / CSH / VIK / Split C-U / Split
    // CSH-VIK). In-memory only: survives Breakdown ⇄ Cashflow toggling while the
    // component stays mounted, resets to Total on reload. No localStorage.
    const [layer, setLayer] = useState(OVERVIEW_LAYER_TOTAL);

    // Monotonic request counter: guards against a late-arriving earlier response
    // (e.g. a slow breakdown fetch resolving after a fast cashflow fetch) overwriting
    // the current view's data.
    const fetchIdRef = useRef(0);

    const selectedColumns = useMemo(() => Object.keys(columns).filter((col) => columns[col].checked));
    const isCashflow = viewMode === VIEW_CASHFLOW;
    const periods = (reporting && reporting.periods) || [];

    // Every numeric (money) column key on the Breakdown table. Name and Total are
    // exempt — Total is the anchor column and never auto-hidden.
    const numericColumnKeys = useMemo(() => {
        const keys = ['cash'];
        markUpCategories.forEach((cat) => keys.push(cat.id + '_value'));
        if (props.vikEnabled) keys.push('vik');
        keys.push('committed', 'uncommitted', 'risk', 'opp', 'forecast');
        return keys;
    }, [props.vikEnabled]);

    // Which numeric columns are entirely zero across the fetched rows. A column is
    // empty iff every revenue + expenditure department row is 0 for it AND the Central
    // Contingency contribution is 0. Recomputes whenever a fetch (incl. search) swaps
    // the data. Central Contingency contributes only to Cash / Uncommitted / Forecast.
    const emptyCols = useMemo(() => {
        const result = {};
        if (!reporting) return result;

        const departmentTotals = [];
        ['revenues', 'expenditure'].forEach((side) => {
            const group = reporting[side];
            if (!group) return;
            Object.keys(group).forEach((key) => {
                if (key === 'totals') return;
                departmentTotals.push((group[key] && group[key].totals) || {});
            });
        });

        const cc = Number(reporting.central_contingency) || 0;
        const ccByKey = { cash: cc, uncommitted: cc, forecast: cc };

        numericColumnKeys.forEach((colKey) => {
            const rowsZero = departmentTotals.every((totals) => (Number(totals[colKey]) || 0) === 0);
            const ccZero = (ccByKey[colKey] || 0) === 0;
            result[colKey] = rowsZero && ccZero;
        });

        return result;
    }, [reporting, numericColumnKeys]);

    // Effective per-column visibility. The manual selector and hide-empty are both
    // HIDE-ONLY filters, ANDed: a column shows only when manually selected AND not
    // (hideEmpty && empty). Neither can force a column back on. Cash / VIK / mark-up
    // columns have no manual toggle (always manually on); VIK also needs vikEnabled.
    const visibleCols = useMemo(() => {
        const show = (colKey, manualKey) => {
            if (manualKey && !selectedColumns.includes(manualKey)) return false;
            if (hideEmpty && emptyCols[colKey]) return false;
            return true;
        };

        const markup = {};
        markUpCategories.forEach((cat) => {
            const colKey = cat.id + '_value';
            markup[colKey] = show(colKey);
        });

        // Agency mode reports Expenses / Mark-ups / Total, so Risks, Opportunities
        // and Forecast (the AFC figure under another label) leave the table — the
        // Financial Summary tiles above drop them at the same time.
        const agency = Boolean(props.agencyMode);

        return {
            cash: show('cash'),
            vik: Boolean(props.vikEnabled) && show('vik'),
            markup,
            committed: show('committed', 'committed'),
            uncommitted: show('uncommitted', 'uncommitted'),
            risk: !agency && show('risk', 'risks'),
            opp: !agency && show('opp', 'opportunities'),
            forecast: !agency && show('forecast', 'forecast'),
        };
    }, [selectedColumns, hideEmpty, emptyCols, props.vikEnabled, props.agencyMode]);

    // Agency-mode row/footer treatment, or null outside it — carries the Margin %
    // basis, so its truthiness is what adds the column.
    const agency = useMemo(() => (props.agencyMode ? { basis: props.marginDisplayBasis } : null), [props.agencyMode, props.marginDisplayBasis]);

    // Layer sub-row / footer labels and the active split's two sub-rows.
    const layerLabels = { committed: $t('Committed'), uncommitted: $t('Uncommitted'), cash: $t('CSH'), vik: $t('VIK') };
    const splitPair = overviewSplitPair(layer, layerLabels);

    // One button in the cashflow layer control. Funding buttons (CSH/VIK/Split
    // CSH-VIK) take a green active accent via the shared .cashflow-layer-control__funding
    // SCSS rule; the rest use the default blue in-nav active style.
    const layerButton = (value, label, funding) => {
        const active = layer === value;
        const cls = ['in-nav__item', '!tw-h-8', funding ? 'cashflow-layer-control__funding' : '', active ? 'active' : 'inactive'].filter(Boolean).join(' ');

        return (
            <button type="button" role="tab" aria-selected={active} className={cls} onClick={() => setLayer(value)}>
                {label}
            </button>
        );
    };

    // Revenue / expenditure department entries (filtered + ordered), reused by the
    // cashflow view for both the rows and the per-side subtotal delimiter rows.
    const revenueEntries =
        reporting && reporting.revenues
            ? Object.entries(reporting.revenues)
                  .filter(([key]) => key !== 'totals')
                  .sort((a, b) => a[1].appends.order - b[1].appends.order)
            : [];
    const expenditureEntries =
        reporting && reporting.expenditure
            ? Object.entries(reporting.expenditure)
                  .filter(([key]) => key !== 'totals')
                  .sort((a, b) => a[1].appends.order - b[1].appends.order)
            : [];

    // Re-fetches on mount and whenever the view mode flips (the cashflow response
    // carries different columns — periods/months/footer — so a fresh fetch is required).
    useEffect(() => {
        fetchReportingOverview();
    }, [viewMode]);

    useEffect(() => {
        localStorage.setItem(eventLocalStorageKey, JSON.stringify(columns));
    }, [columns]);

    useEffect(() => {
        try {
            localStorage.setItem(OVERVIEW_HIDE_EMPTY_KEY, hideEmpty ? 'true' : 'false');
        } catch (e) {
            /* localStorage unavailable — fall back to in-memory only */
        }
    }, [hideEmpty]);

    const fetchReportingOverview = () => {
        setLoading(true);

        const params = { filters };
        if (viewMode === VIEW_CASHFLOW) {
            params.view = VIEW_CASHFLOW;
        }

        const fetchId = ++fetchIdRef.current;

        var axiosPromise = axios
            .get(props.fetchReportingOverviewUrl, {
                params,
            })
            .then(({ data }) => {
                // Ignore a stale response superseded by a newer fetch (view toggled meanwhile).
                if (fetchId !== fetchIdRef.current) {
                    return;
                }
                setReporting(data);
            })
            .catch((e) => console.log(e))
            .finally(() => {
                if (fetchId === fetchIdRef.current) {
                    setLoading(false);
                }
            });
        // loaderInstance.trackAxiosRequest(axiosPromise);
    };

    const handleViewModeChange = (mode) => {
        if (mode === viewMode) {
            return;
        }
        setViewMode(mode);
        try {
            localStorage.setItem(OVERVIEW_VIEW_KEY, mode);
        } catch (e) {
            /* localStorage unavailable — fall back to in-memory only */
        }
    };

    const handleSearchChange = (event) => {
        event.persist();
        setFilters((state) => ({ ...state, search: event.target.value }));
    };

    const handleSearchKeyPress = (event) => {
        if (event.key == 'Enter') {
            fetchReportingOverview();
        }
    };

    return (
        <div className="table__card tw-space-y-6">
            <div className="tw-flex tw-justify-between tw-items-center">
                <h4 className="table__header">{$t('Overview')}</h4>
                <div className="tw-flex tw-items-center tw-gap-x-2.5">
                    {/* Breakdown ⇄ Cashflow (Total) view toggle. */}
                    <SegmentedControl
                        options={[
                            { value: VIEW_BREAKDOWN, label: $t('Breakdown') },
                            { value: VIEW_CASHFLOW, label: $t('Cashflow') },
                        ]}
                        value={viewMode}
                        onChange={handleViewModeChange}
                    />
                    {/* Cashflow layer control — shown only in the Cashflow view. Funding
                        layers (CSH / VIK / Split CSH-VIK) are gated on VIK being enabled and
                        separated from the commitment layers by a divider; with VIK off the
                        single "Split" button drops its C/U suffix. */}
                    {isCashflow && (
                        <div className="in-nav horizontal cashflow-layer-control !tw-w-auto !tw-p-1 !tw-shadow-none tw-border tw-border-grey" role="tablist">
                            {layerButton(OVERVIEW_LAYER_TOTAL, $t('Total'), false)}
                            {layerButton('committed', $t('Committed'), false)}
                            {layerButton('uncommitted', $t('Uncommitted'), false)}
                            {layerButton(OVERVIEW_LAYER_SPLIT, props.vikEnabled ? $t('Split C/U') : $t('Split'), false)}
                            {props.vikEnabled && (
                                <>
                                    <span className="cashflow-layer-control__divider" aria-hidden="true" />
                                    {layerButton('cash', $t('CSH'), true)}
                                    {layerButton('vik', $t('VIK'), true)}
                                    {layerButton(OVERVIEW_LAYER_SPLITFUND, $t('Split CSH/VIK'), true)}
                                </>
                            )}
                        </div>
                    )}
                    {/* Display-unit toggle, hide-empty-columns switch, search + column filter
                        are breakdown-only (the Cashflow view has no financial columns). */}
                    {!isCashflow && (
                        <>
                            <SegmentedControl
                                options={[
                                    { value: DISPLAY_UNIT.M, label: 'm' },
                                    { value: DISPLAY_UNIT.K, label: 'k' },
                                    { value: DISPLAY_UNIT.FULL, label: $t('Full') },
                                ]}
                                value={unit}
                                onChange={setDisplayUnit}
                            />
                            <CheckboxInput
                                id="overview-hide-empty"
                                checked={hideEmpty}
                                onChange={() => setHideEmpty((state) => !state)}
                                label={$t('Hide empty columns')}
                            />
                            <div className="tw-flex tw-items-center tw-space-x-1">
                                <TableSearchInput searchValue={filters.search} handleChange={handleSearchChange} handleKeyPress={handleSearchKeyPress} />
                                <TableFilterModal>
                                    <div className="tw-space-y-4 tw-min-w-96">
                                        <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                            {Object.entries(COLUMNS).map(([key, column]) => {
                                                // The columns agency mode removes get no toggle:
                                                // ticking one could not bring the column back.
                                                if (props.agencyMode && AGENCY_HIDDEN_COLUMNS.includes(key)) return null;

                                                return (
                                                    <div key={`type_${key}`}>
                                                        <CheckboxInput
                                                            id={`type_${key}`}
                                                            defaultChecked={columns[key].checked}
                                                            label={column.label}
                                                            onChange={() => setColumns((state) => ({ ...state, [key]: { checked: !state[key].checked } }))}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </TableFilterModal>
                            </div>
                        </>
                    )}
                    <button className="table__icon-btn tw-text-white" onClick={() => setCollapsed((state) => !state)}>
                        {collapsed ? <PlusIcon className="tw-w-3.5 tw-h-3.5" /> : <MinusIcon className="tw-w-3.5 tw-h-3.5" />}
                    </button>
                </div>
            </div>
            {!collapsed && (
                <div className={`inbud-table_wrapper${!isCashflow ? ' budget-overview-scrollport' : ''}`}>
                    <table
                        className={`inbud-table inbud-table_first-sticky inbud-table_first-shadow tw-table-fixed ${
                            isCashflow ? 'cashflow-table' : 'budget-overview-table'
                        }`}
                    >
                        <thead>
                            {isCashflow ? (
                                <tr>
                                    <th className="tw-w-56">{$t('Name')}</th>
                                    {periods.map((period) => (
                                        <th key={period} className="tw-w-24 inbud-numeric-cell">
                                            {period}
                                        </th>
                                    ))}
                                    <th className="tw-w-24 inbud-numeric-cell">{$t('Total')}</th>
                                </tr>
                            ) : (
                                <tr>
                                    <th className="tw-w-56">{$t('Name')}</th>
                                    {visibleCols.cash && <th className="tw-w-36">{props.agencyMode ? $t('Cost') : $t('Cash')}</th>}
                                    {markUpCategories.map((markUpCategory, index) => {
                                        const colKey = markUpCategory.id + '_value';
                                        return visibleCols.markup[colKey] ? (
                                            <th key={markUpCategory.id ?? index} className="tw-w-36">
                                                {markUpCategory.name}
                                            </th>
                                        ) : null;
                                    })}
                                    {visibleCols.vik && (
                                        <th className="tw-w-36">
                                            <Tooltip placement="bottom" tooltipContent={$t('tooltip.vik')} tooltipType="table">
                                                <div className="tw-flex tw-justify-center tw-items-center tw-gap-x-1">
                                                    <div>{$t('VIK')}</div>
                                                    <img src={CircleHelpIconUrl} className="tw-w-3 tw-h-3" />
                                                </div>
                                            </Tooltip>
                                        </th>
                                    )}
                                    {props.agencyMode && <th className="tw-w-36">{$t('Margin %')}</th>}
                                    <th className="tw-w-36">{$t('Total')}</th>
                                    {visibleCols.committed && <th className="tw-w-36">{$t('Committed')}</th>}
                                    {visibleCols.uncommitted && <th className="tw-w-36">{$t('Uncommitted')}</th>}
                                    {visibleCols.risk && <th className="tw-w-36">{$t('Risks')}</th>}
                                    {visibleCols.opp && <th className="tw-w-36">{$t('Opportunities')}</th>}
                                    {visibleCols.forecast && <th className="tw-w-36">{$t('Forecast')}</th>}
                                </tr>
                            )}
                        </thead>
                        {!isCashflow && !loading && reporting && (
                            <>
                                <tbody>
                                    {reporting.revenues && (
                                        <>
                                            {reporting.revenues &&
                                                Object.entries(reporting.revenues)
                                                    .filter(([key]) => key !== 'totals')
                                                    .sort((a, b) => a[1].appends.order - b[1].appends.order)
                                                    .map(([key, department]) => (
                                                        <ReportingOverviewTableRow
                                                            key={key}
                                                            name={department.appends.name}
                                                            totals={department.totals}
                                                            url={department.appends.url}
                                                            unit={unit}
                                                            visibleCols={visibleCols}
                                                            agency={agency}
                                                            marginNotApplicable
                                                        />
                                                    ))}
                                            {'totals' in reporting.revenues && (
                                                <ReportingOverviewTableFooterRow
                                                    marginNotApplicable
                                                    name={$t('Revenue total')}
                                                    totals={reporting.revenues.totals}
                                                    unit={unit}
                                                    visibleCols={visibleCols}
                                                    agency={agency}
                                                    subtotal
                                                />
                                            )}
                                        </>
                                    )}
                                    {reporting.expenditure &&
                                        Object.entries(reporting.expenditure)
                                            .filter(([key]) => key !== 'totals')
                                            .sort((a, b) => a[1].appends.order - b[1].appends.order)
                                            .map(([key, department]) => (
                                                <ReportingOverviewTableRow
                                                    key={key}
                                                    name={department.appends.name}
                                                    totals={department.totals}
                                                    url={department.appends.url}
                                                    unit={unit}
                                                    visibleCols={visibleCols}
                                                    agency={agency}
                                                />
                                            ))}

                                    {!!reporting.central_contingency && (
                                        <tr>
                                            <td className="tw-font-semibold">{$t('Central Contingency')}</td>
                                            {visibleCols.cash && <OverviewNumericCell value={reporting.central_contingency} unit={unit} />}
                                            {markUpCategories.map((markUpCategory, index) => {
                                                const colKey = markUpCategory.id + '_value';
                                                return visibleCols.markup[colKey] ? (
                                                    <OverviewNumericCell key={markUpCategory.id ?? index} value={0} unit={unit} />
                                                ) : null;
                                            })}
                                            {visibleCols.vik && <OverviewNumericCell value={0} unit={unit} />}
                                            {/* Contingency carries no mark-up, so its Margin % cell reads as a dash. */}
                                            {agency && <OverviewMarginCell notApplicable />}
                                            <OverviewNumericCell value={reporting.central_contingency} unit={unit} className="!tw-bg-[#D8D8D8]" />
                                            {visibleCols.committed && <OverviewNumericCell value={0} unit={unit} />}
                                            {visibleCols.uncommitted && <OverviewNumericCell value={reporting.central_contingency} unit={unit} />}
                                            {visibleCols.risk && <OverviewNumericCell value={0} unit={unit} />}
                                            {visibleCols.opp && <OverviewNumericCell value={0} unit={unit} />}
                                            {visibleCols.forecast && (
                                                <OverviewNumericCell value={reporting.central_contingency} unit={unit} className="!tw-bg-[#D8D8D8]" />
                                            )}
                                        </tr>
                                    )}

                                    {reporting.expenditure && 'totals' in reporting.expenditure && (
                                        <ReportingOverviewTableFooterRow
                                            name={$t('Expenditure Total')}
                                            totals={reporting.expenditure.totals}
                                            unit={unit}
                                            visibleCols={visibleCols}
                                            agency={agency}
                                            subtotal
                                        />
                                    )}
                                </tbody>
                                {'totals' in reporting && (
                                    <tfoot className="[&_td]:!tw-border-t-4 [&_td]:!tw-bg-charcoal">
                                        {/* Always a net figure, in both modes. This page reports revenue
                                            and expenditure together, so the bottom row is a net position
                                            whatever the budget holds — a revenue-free agency budget nets
                                            to a negative, which reads correctly here and would read as
                                            nonsense under a "total" label. The summary pages, which show
                                            one side only, total instead. */}
                                        <ReportingOverviewTableFooterRow
                                            name={$t('Net position')}
                                            totals={reporting.totals}
                                            unit={unit}
                                            visibleCols={visibleCols}
                                            agency={agency}
                                            marginNotApplicable
                                        />
                                    </tfoot>
                                )}
                            </>
                        )}
                        {isCashflow && !loading && reporting && reporting.periods && (
                            <>
                                <tbody>
                                    {/* Revenue section */}
                                    {revenueEntries.map(([key, department]) => (
                                        <ReportingOverviewCashflowRow
                                            key={`rev_${key}`}
                                            name={department.appends.name}
                                            url={department.appends.url}
                                            department={department}
                                            periods={periods}
                                            layer={layer}
                                            splitPair={splitPair}
                                        />
                                    ))}
                                    {reporting.revenues && 'totals' in reporting.revenues && (
                                        <ReportingOverviewCashflowFooterRow
                                            name={$t('Revenue total')}
                                            amounts={sumOverviewCashflowMonths(revenueEntries, periods, layer)}
                                            total={sumOverviewPeriodValues(sumOverviewCashflowMonths(revenueEntries, periods, layer), periods)}
                                            periods={periods}
                                        />
                                    )}
                                    {/* Expenditure section */}
                                    {expenditureEntries.map(([key, department]) => (
                                        <ReportingOverviewCashflowRow
                                            key={`exp_${key}`}
                                            name={department.appends.name}
                                            url={department.appends.url}
                                            department={department}
                                            periods={periods}
                                            layer={layer}
                                            splitPair={splitPair}
                                        />
                                    ))}
                                    {reporting.expenditure && 'totals' in reporting.expenditure && (
                                        <ReportingOverviewCashflowFooterRow
                                            name={$t('Expenditure Total')}
                                            amounts={sumOverviewCashflowMonths(expenditureEntries, periods, layer)}
                                            total={sumOverviewPeriodValues(sumOverviewCashflowMonths(expenditureEntries, periods, layer), periods)}
                                            periods={periods}
                                        />
                                    )}
                                </tbody>
                                {reporting.footer && (
                                    <tfoot className="[&_td]:!tw-border-t-4 [&_td]:!tw-bg-charcoal">
                                        {/* Net (revenue − expenditure) — always. Labelled to match the
                                            Breakdown view's bottom row so one page does not use two
                                            words for the same figure. */}
                                        <ReportingOverviewCashflowFooterRow
                                            name={$t('Net position')}
                                            amounts={reporting.footer.net}
                                            total={sumOverviewPeriodValues(reporting.footer.net, periods)}
                                            periods={periods}
                                        />
                                        {/* Layer net rows per the active selection (splits add both; single
                                            CSH/VIK add one; single Committed/Uncommitted add none). */}
                                        {overviewFooterLayerKeys(layer).map((layerKey) => {
                                            const net =
                                                (reporting.footer.layers && reporting.footer.layers[layerKey] && reporting.footer.layers[layerKey].net) || {};

                                            return (
                                                <ReportingOverviewCashflowFooterRow
                                                    key={`footer_${layerKey}`}
                                                    name={layerLabels[layerKey]}
                                                    amounts={net}
                                                    total={sumOverviewPeriodValues(net, periods)}
                                                    periods={periods}
                                                />
                                            );
                                        })}
                                        {/* Cumulative — reflects the selected layer (splits keep Total). */}
                                        <ReportingOverviewCashflowFooterRow
                                            name={$t('Cumulative')}
                                            amounts={overviewCumulativeMap(reporting.footer, layer)}
                                            total={
                                                periods.length
                                                    ? overviewCashflowValue(overviewCumulativeMap(reporting.footer, layer), periods[periods.length - 1])
                                                    : 0
                                            }
                                            periods={periods}
                                        />
                                    </tfoot>
                                )}
                            </>
                        )}
                    </table>
                    {loading && (
                        <div className="tw-py-4">
                            <LoadingSpinner />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Margin % for one Breakdown row: mark-up over cost, or over cost + mark-up (the
 * Total), on whichever basis the organisation quotes.
 *
 * Derived at render from the two figures on the same row, like every other
 * percentage in this report. Not a money cell — OverviewNumericCell would scale
 * it by the display unit and turn 24.32% into "0.0".
 *
 * An en dash where there is no divisor, and on any row carrying no mark-up at
 * all: the revenue rows share this column, and a 0.00% there would read as a
 * measured margin rather than "does not apply".
 */
function OverviewMarginCell({ totals, basis, notApplicable }) {
    if (notApplicable) {
        return <td className="inbud-numeric-cell overview-zero">{MARGIN_NO_DIVISOR}</td>;
    }

    const markUp = markUpCategories.reduce((sum, cat) => sum + (Number(totals[cat.id + '_value']) || 0), 0);
    const text = marginPercentage(markUp, totals['cash'], basis);

    return <td className={text === MARGIN_NO_DIVISOR ? 'inbud-numeric-cell overview-zero' : 'inbud-numeric-cell'}>{text}</td>;
}

// A numeric money cell for the Breakdown view: unit-aware text via
// formatOverviewFigure, the full value on hover (title), and an `overview-zero`
// marker class on a true zero. Every figure here is an aggregate, so a key the
// totals omit is a zero and takes the dash.
function OverviewNumericCell({ value, unit, className }) {
    const { text, title, isZero } = formatOverviewFigure(value ?? 0, unit);
    const classes = ['inbud-numeric-cell', isZero ? 'overview-zero' : '', className].filter(Boolean).join(' ');

    return (
        <td className={classes} title={title}>
            {text}
        </td>
    );
}

function ReportingOverviewTableRow({ indent, name, totals, url, unit, visibleCols, agency, marginNotApplicable }) {
    return (
        <tr className="!tw-bg-white">
            <td className="tw-capitalize">
                <div className={`tw-font-medium tw-pl-${3 * indent || 0}`}>
                    {url ? (
                        <a className="tw-text-blue hover:tw-text-blue-75" href={url} onClick={(e) => e.stopPropagation()}>
                            {name}
                        </a>
                    ) : (
                        <div>{name}</div>
                    )}
                </div>
            </td>
            {visibleCols.cash && <OverviewNumericCell value={totals['cash']} unit={unit} />}
            {markUpCategories.map((markUpCategory, index) => {
                const colKey = markUpCategory.id + '_value';
                return visibleCols.markup[colKey] ? <OverviewNumericCell key={markUpCategory.id ?? index} value={totals[colKey] || 0} unit={unit} /> : null;
            })}
            {visibleCols.vik && <OverviewNumericCell value={totals['vik']} unit={unit} />}
            {agency && <OverviewMarginCell totals={totals} basis={agency.basis} notApplicable={marginNotApplicable} />}
            <OverviewNumericCell value={totals['total']} unit={unit} className="!tw-bg-[#D8D8D8]" />
            {visibleCols.committed && <OverviewNumericCell value={totals['committed']} unit={unit} />}
            {visibleCols.uncommitted && <OverviewNumericCell value={totals['uncommitted']} unit={unit} />}
            {visibleCols.risk && <OverviewNumericCell value={totals['risk']} unit={unit} />}
            {visibleCols.opp && <OverviewNumericCell value={totals['opp']} unit={unit} />}
            {visibleCols.forecast && <OverviewNumericCell value={totals['forecast']} unit={unit} className="!tw-bg-[#D8D8D8]" />}
        </tr>
    );
}

function ReportingOverviewTableFooterRow({ name, totals, unit, visibleCols, subtotal, agency, marginNotApplicable }) {
    return (
        <tr className={subtotal ? 'inbud-table__footer inbud-table__subtotal' : 'inbud-table__footer'}>
            <td className="tw-capitalize">{name}</td>
            {visibleCols.cash && <OverviewNumericCell value={totals['cash']} unit={unit} />}
            {markUpCategories.map((markUpCategory, index) => {
                const colKey = markUpCategory.id + '_value';
                return visibleCols.markup[colKey] ? <OverviewNumericCell key={markUpCategory.id ?? index} value={totals[colKey] || 0} unit={unit} /> : null;
            })}
            {visibleCols.vik && <OverviewNumericCell value={totals['vik']} unit={unit} />}
            {agency && <OverviewMarginCell totals={totals} basis={agency.basis} notApplicable={marginNotApplicable} />}
            <OverviewNumericCell value={totals['total']} unit={unit} />
            {visibleCols.committed && <OverviewNumericCell value={totals['committed']} unit={unit} />}
            {visibleCols.uncommitted && <OverviewNumericCell value={totals['uncommitted']} unit={unit} />}
            {visibleCols.risk && <OverviewNumericCell value={totals['risk']} unit={unit} />}
            {visibleCols.opp && <OverviewNumericCell value={totals['opp']} unit={unit} />}
            {visibleCols.forecast && <OverviewNumericCell value={totals['forecast']} unit={unit} />}
        </tr>
    );
}

// Parse a single period's value from a period=>amount map. The endpoint now sends
// raw numerics; the parser is kept because it also reads a formatted string safely,
// with the currency's own separators rather than a blanket comma strip.
function overviewCashflowValue(map, period) {
    const raw = (map || {})[period];
    const num = parseLocaleNumber(raw === undefined || raw === null ? 0 : raw);
    return Number.isFinite(num) ? num : 0;
}

// Σ of a period=>amount map across the periods — the trailing Total-column value.
function sumOverviewPeriodValues(map, periods) {
    return periods.reduce((sum, period) => sum + overviewCashflowValue(map, period), 0);
}

// The single layer key a summed row/section subtotal renders under, or null (sum
// `months`, i.e. Total) for Total and both split modes.
function overviewSummingLayer(layer) {
    return layer === OVERVIEW_LAYER_TOTAL || layer === OVERVIEW_LAYER_SPLIT || layer === OVERVIEW_LAYER_SPLITFUND || !layer ? null : layer;
}

// The footer layer-net rows to add for the active layer (splits add both; single
// CSH/VIK add one; single Committed/Uncommitted and Total add none — the asymmetry
// mirrors the DataTable footers).
function overviewFooterLayerKeys(layer) {
    if (layer === OVERVIEW_LAYER_SPLIT) return ['committed', 'uncommitted'];
    if (layer === OVERVIEW_LAYER_SPLITFUND) return ['cash', 'vik'];
    if (layer === 'cash' || layer === 'vik') return [layer];
    return [];
}

// The cumulative map the footer's Cumulative row renders: the selected single
// layer's cumulative, else the combined Total cumulative (splits keep Total).
function overviewCumulativeMap(footer, layer) {
    const single = ['committed', 'uncommitted', 'cash', 'vik'].indexOf(layer) !== -1;
    if (single && footer.layers && footer.layers[layer]) {
        return footer.layers[layer].cumulative || {};
    }
    return footer.cumulative || {};
}

// Per-period sum of a set of overview rows' months (under the active layer) — the
// client-side Revenue/Expenditure side subtotals for the cashflow view (mirrors the
// breakdown overview's "Revenue total" / "Expenditure Total" delimiter rows).
function sumOverviewCashflowMonths(entries, periods, layer) {
    const summingLayer = overviewSummingLayer(layer);
    const totals = {};
    periods.forEach((period) => {
        totals[period] = entries.reduce((sum, [, department]) => {
            const monthly = summingLayer ? (department.layers && department.layers[summingLayer]) || {} : department.months || {};
            const raw = monthly[period];
            const num = parseLocaleNumber(raw === undefined || raw === null ? 0 : raw);
            return sum + (Number.isFinite(num) ? num : 0);
        }, 0);
    });
    return totals;
}

// Cashflow view: a department row with one monthly cell per period, rendered under
// the active layer. Under a split, the main row stays on Total and two labelled
// sub-rows (the split's two layers) are appended beneath it.
function ReportingOverviewCashflowRow({ name, url, department, periods, layer, splitPair }) {
    const monthly = overviewLayerMonths(department, layer);

    return (
        <>
            <tr className="!tw-bg-white">
                <td className="tw-capitalize">
                    <div className="tw-font-medium">
                        {url ? (
                            <a className="tw-text-blue hover:tw-text-blue-75" href={url} onClick={(e) => e.stopPropagation()}>
                                {name}
                            </a>
                        ) : (
                            <div>{name}</div>
                        )}
                    </div>
                </td>
                {periods.map((period) => (
                    <td key={period} className="inbud-numeric-cell">
                        {formatFigure(monthly[period] || 0)}
                    </td>
                ))}
                {/* Trailing per-row Total = sum of the row's months. */}
                <td className="inbud-numeric-cell">{formatFigure(sumOverviewPeriodValues(monthly, periods))}</td>
            </tr>
            {splitPair &&
                splitPair.map(([layerKey, label]) => {
                    const map = (department.layers && department.layers[layerKey]) || {};
                    const dotClass = layerKey === 'cash' || layerKey === 'vik' ? 'cashflow-subrow__dot--funding' : 'cashflow-subrow__dot--commitment';

                    return (
                        <tr key={layerKey} className="cashflow-subrow">
                            <td className="cashflow-subrow__name">
                                <span className="cashflow-subrow__label">
                                    <span className={`cashflow-subrow__dot ${dotClass}`} />
                                    {label}
                                </span>
                            </td>
                            {periods.map((period) => (
                                <td key={period} className="inbud-numeric-cell">
                                    {formatFigure(map[period] || 0)}
                                </td>
                            ))}
                            <td className="inbud-numeric-cell">{formatFigure(sumOverviewPeriodValues(map, periods))}</td>
                        </tr>
                    );
                })}
        </>
    );
}

// Cashflow (Total) view footer: the Revenue total / Expenditure Total section subtotals
// and the Net (revenue − expenditure) / Cumulative rows. `total` is the trailing Total-column
// value — Σ of the row's months for subtotal/Net rows, the final period's value for Cumulative.
function ReportingOverviewCashflowFooterRow({ name, amounts, total, periods }) {
    const monthly = amounts || {};

    return (
        <tr className="inbud-table__footer">
            <td className="tw-capitalize">{name}</td>
            {periods.map((period) => (
                <td key={period} className="inbud-numeric-cell">
                    {formatFigure(monthly[period] || 0)}
                </td>
            ))}
            <td className="inbud-numeric-cell">{formatFigure(total || 0)}</td>
        </tr>
    );
}

if (document.getElementById('ReportingOverviewTable')) {
    var root = document.getElementById('ReportingOverviewTable');
    ReactDOM.render(
        <App>
            <ReportingOverviewTable
                showRevDepartmentsUrl={showRevDepartmentsUrl}
                showTicketingUrl={showTicketingUrl}
                showSponsorshipUrl={showSponsorshipUrl}
                fetchReportingOverviewUrl={fetchReportingOverviewUrl}
                vikEnabled={vikEnabled}
                markUpCategories={markUpCategories}
                markUpCategoriesExists={markUpCategoriesExists}
                eventSlug={eventSlug}
                agencyMode={agencyMode}
                marginDisplayBasis={marginDisplayBasis}
            />
        </App>,
        root,
    );
}
